import { FormEvent, useEffect, useRef, useState } from "react";
import { FunctionalSymbol } from "@quiddity/icons";

type RequestProposal = { id: string; statement: string };
type RequestResolution = {
  disposition: "act" | "defer" | "no_action_at_this_time";
  deferUntil?: string | null;
  position?: { changeCondition?: string | null };
};
type ConnectedState = {
  trajectoryId: string;
  sourceStatement: string;
  requests: RequestProposal[];
  resolutions: Record<string, RequestResolution>;
  concluded: boolean;
};

const SESSION_KEY = "quiddity-quaero-ject-trajectory";

async function responseBody(response: Response) {
  const body = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(typeof body.detail === "string" ? body.detail : `Request failed (${response.status})`);
  }
  return body;
}

export function JectQuaeroHandoff({
  reference,
  resumeTrajectoryId,
}: {
  reference: string | null;
  resumeTrajectoryId: string | null;
}) {
  const [connected, setConnected] = useState<ConnectedState | null>(null);
  const [status, setStatus] = useState("Validating the authorized Act with Ject…");
  const [error, setError] = useState("");
  const [workingRequestId, setWorkingRequestId] = useState("");
  const handoffBootstrap = useRef<Promise<ConnectedState> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        let trajectoryId = resumeTrajectoryId;
        let sourceStatement = "Your participant-authored Act Agreement";
        if (reference) {
          window.history.replaceState(null, "", "/quaero");
          handoffBootstrap.current ??= (async () => {
            const consumed = await responseBody(await fetch("/ject/api/v2/quaero/handoffs/consume", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ reference }),
            }));
            const authorization = consumed.authorization as Record<string, unknown>;
            const consumedTrajectoryId = String(authorization.trajectoryId ?? "");
            const sources = consumed.sourceStatements as string[] | undefined;
            const consumedSource = sources?.[0] || sourceStatement;
            setStatus("Quaero is forming materially distinct “I want to…” proposals…");
            const proposals = await responseBody(await fetch(
              `/ject/api/v2/trajectories/${encodeURIComponent(consumedTrajectoryId)}/quaero/request-proposals`,
              {
                method: "POST",
                credentials: "same-origin",
                headers: {
                  "Content-Type": "application/json",
                  "Idempotency-Key": `quaero-handoff-${crypto.randomUUID()}`,
                },
                body: "{}",
              },
            ));
            const payload = proposals.payload as { requests?: RequestProposal[] };
            return {
              trajectoryId: consumedTrajectoryId,
              sourceStatement: consumedSource,
              requests: payload.requests ?? [],
              resolutions: {},
              concluded: false,
            };
          })();
          const bootstrapped = await handoffBootstrap.current;
          if (!cancelled) {
            window.sessionStorage.setItem(SESSION_KEY, bootstrapped.trajectoryId);
            setConnected(bootstrapped);
          }
        } else if (trajectoryId) {
          setStatus("Restoring the participant-owned request decisions…");
          const trajectory = await responseBody(await fetch(
            `/ject/api/v2/trajectories/${encodeURIComponent(trajectoryId)}`,
            { credentials: "same-origin" },
          ));
          if (!cancelled) {
            const view = trajectory.currentView as {
              requests?: RequestProposal[];
              requestResolutions?: Record<string, RequestResolution>;
              trajectoryConcluded?: boolean;
              dispositionAgreement?: { statement?: string; agreement?: { statement?: string } };
            };
            setConnected({
              trajectoryId,
              sourceStatement:
                view.dispositionAgreement?.agreement?.statement
                || view.dispositionAgreement?.statement
                || sourceStatement,
              requests: view.requests ?? [],
              resolutions: view.requestResolutions ?? {},
              concluded: Boolean(view.trajectoryConcluded),
            });
            if (view.trajectoryConcluded) window.sessionStorage.removeItem(SESSION_KEY);
          }
        }
        if (!cancelled) setStatus("");
      } catch (reason) {
        if (!cancelled) {
          window.sessionStorage.removeItem(SESSION_KEY);
          setStatus("");
          setError(reason instanceof Error ? reason.message : "The authorized Ject handoff could not be opened.");
        }
      }
    };
    void start();
    return () => { cancelled = true; };
  }, [reference, resumeTrajectoryId]);

  const resolveRequest = async (event: FormEvent<HTMLFormElement>, proposal: RequestProposal) => {
    event.preventDefault();
    if (!connected) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const disposition = String(data.get("disposition")) as RequestResolution["disposition"];
    const deferUntil = String(data.get("deferUntil") || "");
    const changeCondition = String(data.get("changeCondition") || "").replace(/\s+/g, " ").trim();
    if (disposition === "defer" && !deferUntil && !changeCondition) {
      setError("Defer requires a reconsideration date, a change condition, or both.");
      return;
    }
    setWorkingRequestId(proposal.id);
    setError("");
    try {
      const result = await responseBody(await fetch(
        `/ject/api/v2/trajectories/${encodeURIComponent(connected.trajectoryId)}/requests/${encodeURIComponent(proposal.id)}/resolutions`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": `quaero-resolution-${crypto.randomUUID()}`,
          },
          body: JSON.stringify({
            id: crypto.randomUUID(),
            disposition,
            effectiveAt: new Date().toISOString(),
            deferUntil: deferUntil ? new Date(`${deferUntil}T12:00:00Z`).toISOString() : null,
            changeCondition: changeCondition || null,
          }),
        },
      ));
      const payload = result.payload as {
        resolution: RequestResolution & { requestId: string };
        trajectoryConcluded?: boolean;
      };
      if (payload.trajectoryConcluded) window.sessionStorage.removeItem(SESSION_KEY);
      setConnected((current) => current ? {
        ...current,
        resolutions: { ...current.resolutions, [proposal.id]: payload.resolution },
        concluded: Boolean(payload.trajectoryConcluded),
      } : current);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Ject could not preserve this request decision.");
    } finally {
      setWorkingRequestId("");
    }
  };

  return (
    <section className="ject-quaero" aria-labelledby="ject-quaero-title">
      <div className="quaero-workspace__intro">
        <p className="eyebrow">Ject-authorized · participant-resolved</p>
        <h2 id="ject-quaero-title">From “I am” to “I want to…”</h2>
        <p>
          Quaero proposes request wording from the current Adjected Act. Ject
          still owns every decision, stance, and conclusion. Nothing here
          assigns a person or executes work.
        </p>
      </div>
      <div className="intake-status" role="status" aria-live="polite">
        {status && <p>{status}</p>}
        {error && <p className="intake-error">{error}</p>}
      </div>
      {error && !connected && (
        <a className="secondary-action" href="https://ject.quiddity.live/">Return to Ject and create a fresh handoff</a>
      )}
      {connected && (
        <>
          <aside className="ject-quaero__source">
            <span>Authorized Act from Ject</span>
            <strong>{connected.sourceStatement}</strong>
          </aside>
          <ol className="request-list">
            {connected.requests.map((proposal, index) => {
              const resolution = connected.resolutions[proposal.id];
              return (
                <li className="request-card" key={proposal.id}>
                  <div className="request-card__meta">
                    <span>Quaero proposal {index + 1}</span>
                    <span>{resolution ? "Resolved in Ject" : "Decision required"}</span>
                  </div>
                  <h4>{proposal.statement}</h4>
                  {resolution ? (
                    <div className="request-card__notice">
                      <FunctionalSymbol semantic="stance.agree.pending" showLabel={false} decorative />
                      <span>
                        {resolution.disposition === "act"
                          ? "Act recorded. Routing may be facilitated; assignment and execution remain unauthorized."
                          : resolution.disposition === "defer"
                            ? "Deferred. Any date or condition triggers reconsideration, never automatic action."
                            : "No action at this time is recorded."}
                      </span>
                    </div>
                  ) : (
                    <form className="question-card" onSubmit={(event) => resolveRequest(event, proposal)}>
                      <fieldset>
                        <legend>Resolve this proposed request</legend>
                        <label><input type="radio" name="disposition" value="act" required defaultChecked /> Act</label>
                        <label><input type="radio" name="disposition" value="defer" /> Defer</label>
                        <label><input type="radio" name="disposition" value="no_action_at_this_time" /> No action at this time</label>
                      </fieldset>
                      <label>Reconsideration date<input type="date" name="deferUntil" /></label>
                      <label>Change condition<textarea name="changeCondition" rows={2} /></label>
                      <button className="primary-action" type="submit" disabled={workingRequestId === proposal.id}>
                        <FunctionalSymbol semantic="common.send" showLabel={false} decorative />
                        {workingRequestId === proposal.id ? "Preserving…" : "Resolve in Ject"}
                      </button>
                    </form>
                  )}
                </li>
              );
            })}
          </ol>
          {connected.concluded && (
            <div className="request-ready" role="status">
              <div>
                <strong>Every proposed request is resolved.</strong>
                <p>This trajectory has reached a participant-owned conclusion for now.</p>
              </div>
              <a className="secondary-action" href="https://ject.quiddity.live/">Return to Ject</a>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function currentJectHandoff() {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const reference = fragment.get("handoff");
  const resumeTrajectoryId = window.sessionStorage.getItem(SESSION_KEY);
  return { reference, resumeTrajectoryId };
}
